/**
 * Phase F2 — Tasks & Follow-Ups UI: shared widgets, modals, and badges.
 * Imported by TasksFollowUps.jsx
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Phone, MessageCircle, Mail, Bell, Calendar as CalIcon, Briefcase, Image as ImageIcon,
  FileText, Award, Users, IndianRupee, Clock, Sparkles, X, Plus, Loader2, Trash2,
  CheckCircle, AlertTriangle, Send, RefreshCw, Paperclip, Zap, Activity, Copy as CopyIcon,
} from "lucide-react";

/* ========== Constants ========== */
export const TASK_TYPES_CUSTOMER = [
  { id: "call",                label: "Call",                Icon: Phone,        color: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "whatsapp",            label: "WhatsApp",            Icon: MessageCircle, color: "bg-green-50 text-green-700 border-green-200" },
  { id: "email",               label: "Email",               Icon: Mail,         color: "bg-orange-50 text-orange-700 border-orange-200" },
  { id: "payment_reminder",    label: "Payment Reminder",    Icon: IndianRupee,  color: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "quotation_reminder",  label: "Quotation Reminder",  Icon: FileText,     color: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "drawing_approval",    label: "Drawing Approval",    Icon: FileText,     color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { id: "site_visit",          label: "Site Visit",          Icon: CalIcon,      color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { id: "meeting",             label: "Meeting",             Icon: Users,        color: "bg-sky-50 text-sky-700 border-sky-200" },
  { id: "review_request",      label: "Review Request",      Icon: Award,        color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "referral_request",    label: "Referral Request",    Icon: Users,        color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "delivery_reminder",   label: "Delivery Reminder",   Icon: ImageIcon,    color: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "custom",              label: "Custom",              Icon: Sparkles,     color: "bg-gray-50 text-gray-700 border-gray-200" },
];

export const TASK_TYPES_INTERNAL = [
  { id: "sales_task",         label: "Sales Task",        Icon: Briefcase, color: "bg-orange-50 text-orange-700 border-orange-200" },
  { id: "designer_task",      label: "Designer Task",     Icon: ImageIcon, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { id: "admin_task",         label: "Admin Task",        Icon: Briefcase, color: "bg-navy-50 text-blue-900 border-blue-200" },
  { id: "accounts_task",      label: "Accounts Task",     Icon: IndianRupee, color: "bg-amber-50 text-amber-800 border-amber-200" },
  { id: "management_task",    label: "Management Task",   Icon: Briefcase, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "personal_reminder",  label: "Personal Reminder", Icon: Bell,      color: "bg-sky-50 text-sky-700 border-sky-200" },
  { id: "general_task",       label: "General Task",      Icon: FileText,  color: "bg-gray-50 text-gray-700 border-gray-200" },
];

export const PRIORITY_META = {
  urgent: { label: "Urgent", color: "bg-rose-500",   text: "text-rose-50",   ring: "ring-rose-300",   chip: "bg-rose-100 text-rose-800 border-rose-300" },
  high:   { label: "High",   color: "bg-orange-500", text: "text-orange-50", ring: "ring-orange-300", chip: "bg-orange-100 text-orange-800 border-orange-300" },
  medium: { label: "Medium", color: "bg-amber-400",  text: "text-amber-900", ring: "ring-amber-200",  chip: "bg-amber-100 text-amber-800 border-amber-300" },
  low:    { label: "Low",    color: "bg-emerald-500",text: "text-emerald-50",ring: "ring-emerald-300",chip: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

export const STATUS_COLORS = {
  pending:   "bg-amber-100 text-amber-700 border-amber-300",
  scheduled: "bg-blue-100 text-blue-700 border-blue-300",
  claimed:   "bg-blue-100 text-blue-700 border-blue-300",
  sent:      "bg-blue-100 text-blue-700 border-blue-300",
  done:      "bg-emerald-100 text-emerald-700 border-emerald-300",
  failed:    "bg-rose-100 text-rose-700 border-rose-300",
  snoozed:   "bg-gray-100 text-gray-600 border-gray-300",
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
};

export const OUTCOME_GROUPS = {
  call:    [{ id: "connected", label: "Connected" }, { id: "busy", label: "Busy" }, { id: "not_answered", label: "Not Answered" }, { id: "switched_off", label: "Switched Off" }, { id: "wrong_number", label: "Wrong Number" }],
  sales:   [{ id: "interested", label: "Interested" }, { id: "need_time", label: "Need Time" }, { id: "call_back_later", label: "Call Back Later" }, { id: "quotation_approved", label: "Quotation Approved" }, { id: "quotation_rejected", label: "Quotation Rejected" }],
  payment: [{ id: "payment_received", label: "Payment Received" }, { id: "payment_promised", label: "Payment Promised" }, { id: "payment_pending", label: "Payment Pending" }],
  project: [{ id: "revision_requested", label: "Revision Requested" }, { id: "approved", label: "Approved" }, { id: "delivered", label: "Delivered" }],
};

export const SNOOZE_PRESETS = [
  { label: "15 min", hours: 0.25 },
  { label: "30 min", hours: 0.5 },
  { label: "1 hour", hours: 1 },
  { label: "Tomorrow", hours: 24 },
  { label: "Next week", hours: 168 },
];

/* ========== Badges ========== */

export function PriorityBadge({ priority = "medium", size = "sm" }) {
  const p = PRIORITY_META[priority] || PRIORITY_META.medium;
  const cls = size === "xs" ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5";
  return (
    <span data-testid={`priority-${priority}`} className={`inline-flex items-center gap-0.5 ${p.chip} ${cls} rounded-full font-bold border uppercase tracking-wide`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${p.color}`} /> {p.label}
    </span>
  );
}

export function TaskTypeChip({ taskType, kind = "customer" }) {
  const list = kind === "internal" ? TASK_TYPES_INTERNAL : TASK_TYPES_CUSTOMER;
  const meta = list.find(t => t.id === taskType) || { label: taskType, Icon: FileText, color: "bg-gray-50 text-gray-700 border-gray-200" };
  const I = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 ${meta.color} text-[10px] px-2 py-0.5 rounded-full font-bold border`}>
      <I className="w-2.5 h-2.5" /> {meta.label}
    </span>
  );
}

/* ========== Dashboard widgets row ========== */

export function DashboardWidgets({ kind = "customer", onWidgetClick }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    api.get(`/sales/follow-ups/dashboard-summary?kind=${kind}`)
      .then(r => setData(r.data || {}))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const widgets = [
    { id: "due_today",        label: "Due Today",       icon: Bell,     color: "bg-orange-500" },
    { id: "overdue",          label: "Overdue",         icon: AlertTriangle, color: "bg-rose-500" },
    { id: "urgent",           label: "Urgent",          icon: AlertTriangle, color: "bg-rose-600" },
    { id: "tomorrow",         label: "Tomorrow",        icon: CalIcon,  color: "bg-blue-500" },
    { id: "this_week",        label: "This Week",       icon: CalIcon,  color: "bg-cyan-500" },
    { id: "pending",          label: "Pending",         icon: Clock,    color: "bg-amber-500" },
    { id: "completed_today",  label: "Completed Today", icon: CheckCircle, color: "bg-emerald-500" },
    { id: "missed",           label: "Missed",          icon: X,        color: "bg-gray-500" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="fu-dashboard-widgets">
      {widgets.map(w => {
        const I = w.icon;
        const v = data[w.id];
        return (
          <button
            key={w.id}
            onClick={() => onWidgetClick?.(w.id)}
            data-testid={`fu-widget-${w.id}`}
            className="bg-white border border-gray-200 hover:border-[#1a3c5e] rounded-xl p-3 text-left transition-all hover:shadow-md group"
          >
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-7 h-7 ${w.color} rounded-lg flex items-center justify-center text-white`}>
                <I className="w-3.5 h-3.5" />
              </div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-gray-600 group-hover:text-[#1a3c5e]">{w.label}</div>
            </div>
            <div className="text-2xl font-bold text-[#1a3c5e]">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (v ?? 0)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ========== Outcome Popup ========== */

export function OutcomeModal({ followUp, onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [subCode, setSubCode] = useState("");
  const [notes, setNotes] = useState("");
  const [autoComplete, setAutoComplete] = useState(true);
  const [activeGroup, setActiveGroup] = useState("call");
  const [saving, setSaving] = useState(false);

  // Smart group default by task_type
  useEffect(() => {
    const tt = followUp?.task_type;
    if (["call"].includes(tt)) setActiveGroup("call");
    else if (["payment_reminder"].includes(tt)) setActiveGroup("payment");
    else if (["drawing_approval", "site_visit", "delivery_reminder"].includes(tt)) setActiveGroup("project");
    else setActiveGroup("sales");
  }, [followUp]);

  const submit = async () => {
    if (!code) { toast.error("Pick an outcome"); return; }
    setSaving(true);
    try {
      await api.post(`/sales/follow-ups/${followUp.id}/outcome`, {
        code, sub_code: subCode || null, notes, auto_complete: autoComplete,
      });
      toast.success("Outcome recorded");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fu-outcome-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Record outcome</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(OUTCOME_GROUPS).map(g => (
              <button key={g} onClick={() => { setActiveGroup(g); setCode(""); }}
                className={`px-2.5 py-1 text-[11px] font-bold rounded-full border ${activeGroup === g ? "bg-[#1a3c5e] text-white border-[#1a3c5e]" : "bg-white border-gray-200 text-gray-600"}`}>
                {g.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {OUTCOME_GROUPS[activeGroup].map(o => (
              <button key={o.id} onClick={() => setCode(o.id)}
                data-testid={`fu-outcome-${o.id}`}
                className={`px-2 py-1.5 text-xs font-bold rounded border ${code === o.id ? "bg-emerald-100 border-emerald-400 text-emerald-800" : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"}`}>
                {o.label}
              </button>
            ))}
          </div>
          <input value={subCode} onChange={e => setSubCode(e.target.value)} placeholder="Sub-code (optional)"
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            placeholder="Notes — what happened?" data-testid="fu-outcome-notes"
            className="w-full px-2 py-2 border border-gray-200 rounded text-sm" />
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={autoComplete} onChange={e => setAutoComplete(e.target.checked)} data-testid="fu-outcome-autocomplete" />
            Mark this task complete after recording outcome
          </label>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving || !code} data-testid="fu-outcome-save"
            className="px-4 py-1.5 text-xs bg-[#1a3c5e] hover:bg-[#0f2940] text-white font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========== Next Follow-Up Modal ========== */

export function NextFollowUpModal({ followUp, onClose, onSaved }) {
  const [inDays, setInDays] = useState(3);
  const [taskType, setTaskType] = useState(followUp?.task_type || "call");
  const [priority, setPriority] = useState(followUp?.priority || "medium");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/sales/follow-ups/${followUp.id}/next`, {
        in_days: inDays, task_type: taskType, priority, note,
      });
      toast.success("Next follow-up created");
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fu-next-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e]">Create next follow-up</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-600">In days</label>
              <input type="number" min="1" value={inDays} onChange={e => setInDays(Number(e.target.value))}
                data-testid="fu-next-days"
                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-600">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm">
                {Object.keys(PRIORITY_META).map(p => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">Type</label>
            <select value={taskType} onChange={e => setTaskType(e.target.value)} data-testid="fu-next-type"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm">
              {TASK_TYPES_CUSTOMER.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
            placeholder="Note (carried into the new task)"
            className="w-full px-2 py-2 border border-gray-200 rounded text-sm" />
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="fu-next-save"
            className="px-4 py-1.5 text-xs bg-[#1a3c5e] hover:bg-[#0f2940] text-white font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========== AI Suggestion modal ========== */

export function AISuggestModal({ leadId, onClose, onApply }) {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("");

  useEffect(() => {
    api.post("/sales/follow-ups/ai-suggest", { lead_id: leadId })
      .then(r => { setSuggestions(r.data.suggestions || []); setSource(r.data.source || ""); })
      .catch(e => toast.error(e?.response?.data?.detail || "AI suggest failed"))
      .finally(() => setLoading(false));
  }, [leadId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fu-ai-suggest-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" /> AI Follow-up Suggestions
            {source && <span className="text-[10px] font-normal text-gray-500 italic">({source})</span>}
          </h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 max-h-[60vh] overflow-y-auto">
          {loading && <div className="text-center text-gray-400 py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
          {!loading && (suggestions || []).length === 0 && (
            <div className="text-center text-gray-400 py-8 text-sm">No suggestions right now.</div>
          )}
          <div className="space-y-2">
            {(suggestions || []).map((s, i) => {
              const meta = TASK_TYPES_CUSTOMER.find(t => t.id === s.task_type) || {};
              const I = meta.Icon || FileText;
              return (
                <div key={i} className="border border-gray-200 rounded-xl p-3 hover:border-purple-300 hover:bg-purple-50 transition-all" data-testid={`fu-ai-sugg-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <TaskTypeChip taskType={s.task_type} />
                        <PriorityBadge priority={s.priority} />
                        <span className="text-[10px] text-gray-500">in {s.in_days} day{s.in_days !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="text-xs text-gray-700">{s.reason}</div>
                      {s.note && <div className="text-[11px] text-gray-500 italic mt-1">&quot;{s.note}&quot;</div>}
                    </div>
                    <button onClick={() => onApply?.(s)} data-testid={`fu-ai-apply-${i}`}
                      className="px-2 py-1 text-[10px] bg-purple-600 hover:bg-purple-700 text-white font-bold rounded inline-flex items-center gap-0.5 flex-shrink-0">
                      <Plus className="w-3 h-3" /> Create
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="px-5 py-2 border-t bg-gray-50 text-[10px] text-gray-500 italic">
          AI suggestions only — nothing is created until you click Create.
        </div>
      </div>
    </div>
  );
}

/* ========== Notes timeline drawer ========== */

export function NotesTimelinePanel({ followUp, onClose, onUpdated }) {
  const [timeline, setTimeline] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/sales/follow-ups/${followUp.id}/timeline`)
      .then(r => setTimeline(r.data.events || []))
      .finally(() => setLoading(false));
  }, [followUp.id]);
  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      await api.post(`/sales/follow-ups/${followUp.id}/note`, { text: newNote });
      setNewNote("");
      load();
      onUpdated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const ICON_MAP = {
    note: { I: FileText, color: "bg-blue-100 text-blue-600", label: "Note" },
    outcome: { I: CheckCircle, color: "bg-emerald-100 text-emerald-600", label: "Outcome" },
    execution: { I: Send, color: "bg-purple-100 text-purple-600", label: "Execution" },
    escalation: { I: AlertTriangle, color: "bg-rose-100 text-rose-600", label: "Escalation" },
    log: { I: RefreshCw, color: "bg-gray-100 text-gray-600", label: "Event" },
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="fu-timeline-panel">
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-md h-full overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Clock className="w-4 h-4" /> Activity Timeline</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <input value={newNote} onChange={e => setNewNote(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addNote()}
              placeholder="Add a note…" data-testid="fu-note-input"
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm" />
            <button onClick={addNote} disabled={saving || !newNote.trim()} data-testid="fu-note-save"
              className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-xs font-bold rounded disabled:opacity-60">
              Add
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {loading && <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>}
          {!loading && timeline.length === 0 && <div className="text-center text-gray-400 py-6 text-sm">No activity yet.</div>}
          {timeline.slice().reverse().map((ev, i) => {
            const meta = ICON_MAP[ev.type] || ICON_MAP.log;
            const I = meta.I;
            return (
              <div key={i} className="flex gap-2.5">
                <div className={`w-7 h-7 ${meta.color} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <I className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[11px] font-bold text-[#1a3c5e]">{meta.label}{ev.actor_name ? ` · ${ev.actor_name}` : ""}</div>
                    <div className="text-[10px] text-gray-400 flex-shrink-0">{ev.at ? new Date(ev.at).toLocaleString() : ""}</div>
                  </div>
                  {ev.type === "note" && <div className="text-xs text-gray-700 mt-0.5">{ev.details?.text}</div>}
                  {ev.type === "outcome" && (
                    <div className="text-xs text-gray-700 mt-0.5">
                      <span className="font-bold">{ev.details?.code}</span>
                      {ev.details?.sub_code ? <span className="text-gray-500"> · {ev.details.sub_code}</span> : null}
                      {ev.details?.notes && <div className="italic text-gray-600 mt-0.5">&quot;{ev.details.notes}&quot;</div>}
                    </div>
                  )}
                  {ev.type === "execution" && (
                    <div className="text-[11px] mt-0.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${ev.details?.status === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {ev.details?.status} · retry {ev.details?.retry_no || 0}
                      </span>
                      {ev.details?.error && <div className="text-rose-600 italic mt-0.5">{ev.details.error}</div>}
                    </div>
                  )}
                  {ev.type === "escalation" && (
                    <div className="text-xs text-rose-700 mt-0.5">Escalated to level {ev.details?.level}</div>
                  )}
                  {ev.type === "log" && <div className="text-[11px] text-gray-600 mt-0.5">{ev.details?.event_type}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ========== Attachment uploader (inline link helper) ========== */

export function AttachmentUploader({ followUp, onClose, onSaved }) {
  const [url, setUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [kind, setKind] = useState("file");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!url || !filename) { toast.error("URL aur filename dono chahiye"); return; }
    setSaving(true);
    try {
      await api.post(`/sales/follow-ups/${followUp.id}/attachment`, { url, filename, kind });
      toast.success("Attached");
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fu-attach-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Paperclip className="w-4 h-4" /> Attach file</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 space-y-2">
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="File URL (paste link from File Library / S3 / Drive)"
            data-testid="fu-attach-url" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
          <input value={filename} onChange={e => setFilename(e.target.value)} placeholder="Filename (e.g. brochure.pdf)"
            data-testid="fu-attach-name" className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
          <select value={kind} onChange={e => setKind(e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm">
            {["image", "pdf", "dwg", "excel", "doc", "video", "other", "file"].map(k => <option key={k}>{k}</option>)}
          </select>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="fu-attach-save"
            className="px-4 py-1.5 text-xs bg-[#1a3c5e] hover:bg-[#0f2940] text-white font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Attach
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========== Phase F1 (iter 36) — Delivery Details panel & Test Execute ========== */

const CHANNEL_META = {
  in_app:   { Icon: Bell,          label: "In-App",   tint: "bg-blue-50 border-blue-200 text-blue-900" },
  whatsapp: { Icon: MessageCircle, label: "WhatsApp", tint: "bg-emerald-50 border-emerald-200 text-emerald-900" },
  email:    { Icon: Mail,          label: "Email",    tint: "bg-orange-50 border-orange-200 text-orange-900" },
  sms:      { Icon: MessageCircle, label: "SMS",      tint: "bg-cyan-50 border-cyan-200 text-cyan-900" },
};
const STATUS_PILL = {
  pending:   "bg-amber-100 text-amber-700 border-amber-300",
  sent:      "bg-blue-100 text-blue-700 border-blue-300",
  delivered: "bg-emerald-100 text-emerald-700 border-emerald-300",
  read:      "bg-emerald-200 text-emerald-800 border-emerald-400",
  opened:    "bg-emerald-200 text-emerald-800 border-emerald-400",
  failed:    "bg-rose-100 text-rose-700 border-rose-300",
};

function ChannelStatusPill({ status }) {
  const cls = STATUS_PILL[status] || "bg-gray-100 text-gray-700 border-gray-300";
  return <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${cls}`}>{status || "—"}</span>;
}

function ChannelCard({ ch, data }) {
  const meta = CHANNEL_META[ch] || { Icon: FileText, label: ch, tint: "bg-gray-50 border-gray-200 text-gray-800" };
  const I = meta.Icon;
  return (
    <div className={`border rounded-xl p-2.5 ${meta.tint}`} data-testid={`fu-delivery-ch-${ch}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 font-bold text-xs"><I className="w-3.5 h-3.5" /> {meta.label}</div>
        <ChannelStatusPill status={data?.status} />
      </div>
      <div className="space-y-0.5 text-[10.5px] font-mono break-all">
        {data?.notification_id   && <div><span className="text-gray-500">notification_id:</span> {data.notification_id}</div>}
        {data?.recipient_user_id && <div><span className="text-gray-500">recipient:</span> {data.recipient_user_id}</div>}
        {data?.wa_message_id     && <div><span className="text-gray-500">wa_id:</span> {data.wa_message_id}</div>}
        {data?.template_name     && <div><span className="text-gray-500">template:</span> {data.template_name}</div>}
        {data?.phone             && <div><span className="text-gray-500">phone:</span> {data.phone}</div>}
        {data?.email_id          && <div><span className="text-gray-500">resend_id:</span> {data.email_id}</div>}
        {data?.to                && <div><span className="text-gray-500">to:</span> {data.to}</div>}
        {data?.subject           && <div><span className="text-gray-500">subject:</span> {data.subject}</div>}
        {data?.sent_at           && <div><span className="text-gray-500">sent_at:</span> {data.sent_at}</div>}
        {data?.delivered_at      && <div><span className="text-gray-500">delivered_at:</span> {data.delivered_at}</div>}
        {data?.read_at           && <div><span className="text-gray-500">read_at:</span> {data.read_at}</div>}
        {data?.has_attachments   && <div><span className="text-gray-500">attachments:</span> {data.attachment_count}</div>}
        {data?.error && (
          <div className="bg-white border border-rose-300 rounded p-1.5 text-rose-700 mt-1">
            <div className="text-[9px] uppercase font-bold text-rose-500">error</div>
            <div className="text-[10.5px]">{data.error}</div>
            {data.hint && <div className="text-[10px] italic text-rose-500 mt-0.5">hint: {data.hint}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export function DeliveryDetailsPanel({ followUp, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [liveExec, setLiveExec] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/sales/follow-ups/${followUp.id}/delivery-details`)
      .then(r => setDetails(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load delivery details"))
      .finally(() => setLoading(false));
  }, [followUp.id]);
  useEffect(() => { load(); }, [load]);

  const testExecute = async () => {
    if (!window.confirm("Run this follow-up IMMEDIATELY? This sends real messages to the lead's channels.")) return;
    setExecuting(true);
    setLiveExec(null);
    try {
      const r = await api.post(`/sales/follow-ups/${followUp.id}/test-execute`);
      setLiveExec(r.data);
      if (r.data.ok) toast.success(`Sent on ${r.data.delivered_channels.join(", ") || "—"}`);
      else toast.error(`All channels failed (retry ${r.data.retries})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test execute failed");
    } finally {
      setExecuting(false);
    }
  };

  const copyJson = (obj) => {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
    toast.success("Copied to clipboard");
  };

  const channels = details?.channel_results || {};
  const channelKeys = Object.keys(channels);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="fu-delivery-panel">
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-xl h-full overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Activity className="w-4 h-4" /> Delivery Details</h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-2 flex-wrap">
          <ChannelStatusPill status={details?.status} />
          <span className="text-[10px] text-gray-500">retries: <b>{details?.retries ?? 0}</b></span>
          {details?.delivered_channels?.length > 0 && (
            <span className="text-[10px] text-emerald-700 font-bold">via {details.delivered_channels.join(", ")}</span>
          )}
          <div className="flex-1" />
          <button onClick={testExecute} disabled={executing} data-testid="fu-test-execute-btn"
            className="px-3 py-1.5 text-xs bg-[#1a3c5e] hover:bg-[#0f2940] text-white font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {executing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 text-yellow-300" />}
            Test Execute
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {loading && <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>}
          {!loading && (
            <>
              {details?.error && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-2.5 text-xs">
                  <div className="font-bold text-rose-700">Last error</div>
                  <div className="text-rose-600 font-mono text-[11px]">{details.error}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-600 mb-2">Per-channel status</div>
                {channelKeys.length === 0 && (
                  <div className="text-xs text-gray-400 italic">No execution yet. Click &quot;Test Execute&quot; to send now and capture live status.</div>
                )}
                <div className="space-y-2">
                  {channelKeys.map(ch => <ChannelCard key={ch} ch={ch} data={channels[ch]} />)}
                </div>
              </div>
              {liveExec && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 text-xs">
                  <div className="font-bold text-yellow-800 flex items-center gap-1.5 mb-2">
                    <Zap className="w-3.5 h-3.5" /> Live execution result
                  </div>
                  <pre className="bg-white border border-yellow-200 rounded p-2 text-[10px] overflow-x-auto max-h-80" data-testid="fu-test-execute-result">{JSON.stringify(liveExec, null, 2)}</pre>
                  <button onClick={() => copyJson(liveExec)} className="mt-2 inline-flex items-center gap-1 text-[10px] text-yellow-800 font-bold">
                    <CopyIcon className="w-2.5 h-2.5" /> Copy JSON
                  </button>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-600 mb-2">Execution history ({(details?.exec_logs || []).length})</div>
                <div className="space-y-1.5">
                  {(details?.exec_logs || []).slice().reverse().map((log, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-2 text-[10.5px]">
                      <div className="flex justify-between mb-1">
                        <span className={`font-bold ${log.status === "ok" ? "text-emerald-700" : "text-rose-700"}`}>
                          {log.status?.toUpperCase()} {log.via ? `· ${log.via}` : ""} · retry {log.retry_no ?? 0}
                        </span>
                        <span className="text-gray-500 font-mono">{log.finished_at}</span>
                      </div>
                      {log.error && <div className="text-rose-600 italic">{log.error}</div>}
                      {log.delivered_channels?.length > 0 && (
                        <div className="text-emerald-700 text-[10px]">delivered via: {log.delivered_channels.join(", ")}</div>
                      )}
                    </div>
                  ))}
                  {(details?.exec_logs || []).length === 0 && <div className="text-xs text-gray-400 italic">No executions logged yet.</div>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function QuickTemplateChips({ kind = "customer", onPick }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    api.get("/admin/follow-up-templates")
      .then(r => setTemplates((r.data.rows || []).filter(t => t.kind === kind)))
      .catch(() => setTemplates([]));
  }, [kind]);

  if (!templates.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="fu-quick-templates">
      <span className="text-[10px] uppercase font-bold text-gray-500">Quick templates:</span>
      {templates.map(t => (
        <button key={t.id} onClick={() => onPick?.(t)} data-testid={`fu-quick-tmpl-${t.id}`}
          className="px-2 py-1 text-[10px] bg-[#fef3e2] hover:bg-[#fde7c4] border border-[#F7941D]/40 text-[#1a3c5e] font-bold rounded-full inline-flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5 text-[#F7941D]" /> {t.name}
        </button>
      ))}
    </div>
  );
}
